import { useEffect, useState } from "react";
import LabelInput from "../labelInput";
import FieldStack from "../FieldStack";
import { assetsPath } from "../../utils/assetsPath";
import {
  fetchMakes,
  fetchModels,
  fetchVehicleData,
} from "../../services/simpleListItemsService";

const ManualVehicleLookup = ({
  register,
  watch,
  control,
  trigger,
  errors,
  STATE,
  setValue,
  resetLookupData,
  setErrorVR,
  isRegistrationUnknown,
  onVehicleSelect,
  setManualVehicleEntry,
  showLookupQuestions = true,
}) => {
  const [makeOptions, setMakeOptions] = useState([]);
  const [makesLoading, setMakesLoading] = useState(false);
  const [modelOptions, setModelOptions] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [yearOptions, setYearOptions] = useState([]);
  const [doorOptions, setDoorOptions] = useState([]);
  const [vehiclesList, setVehiclesList] = useState([]);
  const [vehicleListIsLoading, setVehicleListIsLoading] = useState(false);

  const manualMake = watch("manualMake")?.value;
  const manualModel = watch("manualModel")?.value;
  const manualYear = watch("manualYear")?.value || "";
  const manualDoor = watch("manualDoor")?.value || "";

  const convertArrayFormat = (array) => {
    return Array.isArray(array)
      ? array.map((item) => ({
          value: item.listItemId,
          label: item.listItemDescription,
        }))
      : [];
  };

  const convertArrayFormat2 = (array) => {
    return Array.isArray(array)
      ? array.map((item) => ({
          value: item,
          label: item,
        }))
      : [];
  };

  const fetchData = async () => {
    if (manualMake && manualModel) {
      try {
        setVehicleListIsLoading(true);
        const result = await fetchVehicleData({
          makeSearch: manualMake,
          modelSearch: manualModel,
          vehicleYearValue: manualYear,
          vehicleDoorvalue: manualDoor,
        });
        if (!manualYear) {
          setYearOptions(result.years);
          setDoorOptions(result.doors);
        } else {
          setDoorOptions(result.doors);
          if (result.isSearchSuccess) {
            setVehiclesList(result.vehicles);
          }
        }
      } catch (error) {
        console.error("Error fetching vehicle data:", error);
      } finally {
        setVehicleListIsLoading(false);
      }
    }
  };

  useEffect(() => {
    const fetchAndSetMakes = async () => {
      setErrorVR("");
      try {
        setMakesLoading(true);
        const makes = await fetchMakes();
        setMakeOptions(convertArrayFormat(makes));
        // Pre-populate manualMake if make is a string
        const currentMake = watch("make");
        if (currentMake && typeof currentMake === "string") {
          const matchingOption = convertArrayFormat(makes).find(
            (option) =>
              option.label.toLowerCase() === currentMake.toLowerCase(),
          );
          if (matchingOption) {
            setValue("manualMake", matchingOption);
          }
        }
      } catch (error) {
        console.error("Error fetching makes:", error);
      } finally {
        setMakesLoading(false);
      }
    };

    fetchAndSetMakes();
  }, []);

  useEffect(() => {
    const fetchAndSetModels = async () => {
      setErrorVR("");
      try {
        setModelsLoading(true);
        if (manualMake) {
          const models = await fetchModels(manualMake);
          setModelOptions(convertArrayFormat(models));
          // Pre-populate manualModel if model is a string
          const currentModel = watch("model");
          if (currentModel && typeof currentModel === "string") {
            const matchingOption = convertArrayFormat(models).find(
              (option) =>
                option.label.toLowerCase() === currentModel.toLowerCase(),
            );
            if (matchingOption) {
              setValue("manualModel", matchingOption);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching models:", error);
      } finally {
        setModelsLoading(false);
      }
    };

    if (manualMake) {
      setValue("manualModel", null);
      setValue("manualYear", null);
      setValue("manualDoor", null);
      setYearOptions([]);
      setDoorOptions([]);
      setVehiclesList([]);
      fetchAndSetModels();
    }
  }, [manualMake]);

  useEffect(() => {
    if (manualModel) {
      fetchData();
    }
  }, [manualModel]);

  useEffect(() => {
    if (manualYear && manualModel) {
      setDoorOptions([]);
      fetchData();
    }
  }, [manualYear]);

  useEffect(() => {
    if (manualDoor && manualModel && manualYear) {
      fetchData();
    }
  }, [manualDoor]);

  const handleSelectVehicle = (index, item) => {
    const selectedManualDoor = watch("manualDoor");

    setErrorVR("");
    resetLookupData();
    setValue("abiCode", item.abiCode);
    setValue("make", item.abiMake);
    setValue("model", item.abiModel);
    setValue("noOfSeats", parseInt(item.seats));
    setValue("cubicCapacity", item.abiCC);
    setValue("yearOfManufacture", item.year);
    setValue("bodyTypeId", item.bodyTypeId);
    setValue("fuelTypeId", item.abiFuel);
    setValue("gearboxId", item.abiShift);
    setValue("registeredDate", item.dateFirstRegistered);
    setValue("preVehicleSearch", item.abiVehicleInfoDesc);
    onVehicleSelect?.(index, item, { manualDoor: selectedManualDoor });
    setManualVehicleEntry(false);
    if (showLookupQuestions) {
      STATE.setShowBasicLookupQuestions(true);
      STATE.setShowExtraLookupQuestions(true);
    }
  };

  return (
    <FieldStack className="tw-w-full">
      <p className="tw-m-0">
        {isRegistrationUnknown
          ? "Please select your vehicle manually from the options below."
          : "Unable to match your vehicle. Please select manually from the options below."}
      </p>
      <LabelInput
        id="manualMake"
        label="Vehicle make"
        type="select"
        options={makeOptions}
        control={control}
        placeholder="Enter make"
        register={register("manualMake", {
          required: "Required",
        })}
        errors={errors.manualMake}
        watch={watch("manualMake")}
        trigger={trigger}
        isSearchable={true}
        isLoading={makesLoading}
      />
      <LabelInput
        id="manualModel"
        label="Vehicle model"
        control={control}
        options={modelOptions}
        type="select"
        placeholder="Enter model"
        register={register("manualModel", {
          required: "Required",
        })}
        errors={errors.manualModel}
        watch={watch("manualModel")}
        trigger={trigger}
        isSearchable={true}
        isLoading={modelsLoading || makesLoading}
      />
      {watch("manualModel") && (
        <>
          <LabelInput
            id="manualYear"
            label="Year"
            control={control}
            options={convertArrayFormat2(yearOptions)}
            type="select"
            placeholder="Enter year"
            register={register("manualYear", {
              required: "Required",
            })}
            errors={errors.manualYear}
            watch={watch("manualYear")}
            trigger={trigger}
            isSearchable={true}
            isLoading={vehicleListIsLoading}
          />
          {watch("manualYear") && (
            <>
              {Array.isArray(doorOptions) && doorOptions.length > 0 && (
                <LabelInput
                  id="manualDoor"
                  label="Doors"
                  control={control}
                  options={convertArrayFormat2(doorOptions)}
                  type="select"
                  placeholder="Enter doors"
                  register={register("manualDoor", {
                    required: "Required",
                  })}
                  errors={errors.manualDoor}
                  watch={watch("manualDoor")}
                  trigger={trigger}
                  isLoading={vehicleListIsLoading}
                />
              )}
              {vehicleListIsLoading ? (
              <div className="tw-rounded-xl tw-bg-dis-sky-tint tw-px-2.5 tw-py-10 tw-text-center tw-font-medium tw-text-dis-deep-blue">Loading vehicles...</div>
              ) : (
                vehiclesList.length > 0 && (
                  <div className="tw-mt-[30px] tw-mb-2.5 tw-w-full tw-border-t tw-border-[#eee] tw-pt-5">
                    <div className="tw-mb-[15px] tw-flex tw-w-full tw-items-end tw-justify-between tw-px-[5px] tw-text-base tw-font-semibold">
                      <div>Select your vehicle</div>
                      <div className="tw-mb-0.5 tw-text-xs tw-text-[#4a5568]">
                        {vehiclesList.length} Vehicle(s) Found
                      </div>
                    </div>
                    <div className="tw-flex tw-max-h-[400px] tw-flex-col tw-gap-2.5 tw-overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:tw-hidden">
                      {vehiclesList.map((item, index) => (
                        <button
                          key={index}
                        className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-xl tw-border-2 tw-border-transparent tw-bg-dis-field tw-p-3 tw-text-left tw-text-dis-navy tw-transition-colors hover:tw-cursor-pointer hover:tw-border-dis-border-strong hover:tw-bg-dis-field-hover"
                          onClick={() => handleSelectVehicle(index, item)}
                          type="button"
                        >
                          <div className="tw-flex tw-h-[50px] tw-min-w-[50px] tw-items-center tw-justify-center tw-rounded-[10px] tw-border tw-border-dis-border tw-bg-white/40">
                            <img
                              className="tw-h-[35px] tw-w-[35px] tw-opacity-50"
                              src={assetsPath.carIcon}
                            />
                          </div>
                          <div className="tw-flex-1">
                            <div className="tw-text-base tw-font-medium tw-text-dis-navy">
                              {item.abiMake ? `${item.abiMake} -` : "N/A"}{" "}
                              {item.abiModel || "N/A"}
                            </div>
                            <div className="tw-mt-1 tw-text-[14px] tw-text-dis-text-muted">
                              {item.abiVehicleInfoDesc || "N/A"}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )
              )}
            </>
          )}
        </>
      )}
    </FieldStack>
  );
};

export default ManualVehicleLookup;
